import { useEffect, useMemo } from "react";
import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";

import THEME_DEFAULT_DATA from "containers/admin/clients/client/theme-page/theme-default-data.json";

import { useGoogleFonts } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/google-fonts/use-google-fonts";
import { useAdobeFonts } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/adobe-fonts/use-adobe-fonts";
import { createAdobeFontSlugToCssNameMap } from "containers/admin/clients/client/theme-page/preview/helpers/bee-data-formatter/bee-data-formatter-helper";

import {
  LayoutResponsivePadding,
  PaddingOptions,
} from "containers/admin/clients/client/theme-page/preview/preview.types";

type AlignmentOptions = "left" | "center" | "right" | "justify";

const PreviewAreaContent = ({
  isMobileView,
  clientName,
}: {
  isMobileView: boolean;
  clientName: string;
}) => {
  const {
    colors,
    typography,
    button,
    fonts,
    banner,
    layout,
    isFallbackFontApplied,
    adobeFontsList,
  } = useThemePreviewContext();
  const { loadGoogleFont } = useGoogleFonts();
  const { appendStylesheet } = useAdobeFonts();

  const { primary, secondary, tertiary, intense, bold, soft, subtle, white } = colors!;
  const { heading, subHeading, paragraph, buttonLabel } = typography!;
  const { padding, width, alignment, backgroundColor, borderRadius, borderStyle } = button!;
  const { adobeFontsProjectId } = fonts!;
  const { imageUrl } = banner!;
  const { contentArea, contentBlock } = layout!;

  const defaultTypography = THEME_DEFAULT_DATA.typography;
  const defaultButton = THEME_DEFAULT_DATA.button;
  const defaultFontFamily = "Figtree";

  // Create font mapping for preview
  const adobeFontMap = useMemo(
    () => createAdobeFontSlugToCssNameMap(adobeFontsList || []),
    [adobeFontsList],
  );

  // Helper to get the correct font family for preview
  const getFontFamily = (fontValue: string, fallbackValue: string) => {
    if (isFallbackFontApplied) {
      return fallbackValue;
    }
    // First check if it is an Adobe, then google font, then default
    return adobeFontMap.get(fontValue) || fontValue || defaultFontFamily;
  };

  const getDevicePadding = (padding: LayoutResponsivePadding) => {
    if (isMobileView) {
      return getSimplePadding(padding.mobile);
    }
    return getSimplePadding(padding.desktop);
  };

  const getSimplePadding = (padding: PaddingOptions) => {
    return `${padding.top ?? 0}px ${padding.right ?? 0}px ${padding.bottom ?? 0}px ${padding.left ?? 0}px`;
  };

  // Helper to get correct contentBlock padding groups
  const getLayoutPadding = (padding: LayoutResponsivePadding, blockType?: string) => {
    if (blockType && contentBlock.group.value === "all-blocks") {
      return getDevicePadding(contentBlock.allBlocks);
    }

    return getDevicePadding(padding);
  };

  const getButtonWidth = () => {
    const widthName = width?.name?.value;

    switch (widthName) {
      case "fixed":
        return `${width?.custom ?? defaultButton?.width?.custom}%`;
      case "fill":
        return "100%";
      default:
        return "auto";
    }
  };

  // Load google font stylesheets
  useEffect(() => {
    const loadGoogleFonts = async () => {
      if (!fonts?.googleFonts) return;

      for (const font of fonts.googleFonts) {
        const fontName = typeof font === "string" ? font : font.value;
        if (fontName) {
          try {
            await loadGoogleFont(fontName);
          } catch (err) {
            console.error(`Failed to load Google Font: ${font}`, err);
          }
        }
      }
    };

    loadGoogleFonts();
    // eslint-disable-next-line
  }, [loadGoogleFont]);

  // Load Adobe Fonts stylesheet
  useEffect(() => {
    const loadAdobeFonts = async () => {
      if (
        adobeFontsProjectId &&
        !document.querySelector(`link[href="https://use.typekit.net/${adobeFontsProjectId}.css"]`)
      ) {
        try {
          await appendStylesheet(adobeFontsProjectId);
        } catch (err) {
          console.error("Error loading Adobe Fonts:", err);
        }
      }
    };

    loadAdobeFonts();
    // eslint-disable-next-line
  }, []);

  const getColorValue = (color: string, defaultColor: string, customColor?: string) => {
    switch (color) {
      case "none":
        return "transparent";
      case "custom":
        return customColor || defaultColor;
      default:
        return colorMap[color];
    }
  };

  const colorMap: Record<string, string> = {
    primary,
    secondary,
    tertiary,
    intense,
    bold,
    soft,
    subtle,
    white,
  };

  const parapgraphStyles = {
    fontFamily: getFontFamily(paragraph.fontFamily.value, paragraph.fallbackFontFamily.value),
    fontSize: paragraph.fontSize.value || defaultTypography.paragraph.fontSize.value,
    textAlign: (paragraph.alignment || defaultTypography.paragraph.alignment) as AlignmentOptions,
    lineHeight: paragraph.lineHeight.value || defaultTypography.paragraph.lineHeight.value,
    fontWeight: paragraph.fontWeight.value || defaultTypography.paragraph.fontWeight.value,
    color:
      paragraph.textColor.value === "custom"
        ? paragraph.customColor || defaultTypography.paragraph.textColor.value
        : colorMap[paragraph.textColor.value],
    padding: getLayoutPadding(contentBlock.paragraph, "paragraph"),
  };

  return (
    <div
      className="flex flex-col bg-white border-t border-neutrals-200"
      style={{ padding: getSimplePadding(contentArea) }}>
      {imageUrl && (
        <img src={imageUrl} alt="Banner" className="w-full h-auto max-w-full object-cover" />
      )}
      <div>
        <div
          style={{
            fontFamily: getFontFamily(heading.fontFamily.value, heading.fallbackFontFamily.value),
            fontSize: heading.fontSize.value || defaultTypography.heading.fontSize.value,
            textAlign: (heading.alignment ||
              defaultTypography.heading.alignment) as AlignmentOptions,
            lineHeight: heading.lineHeight.value || defaultTypography.heading.lineHeight.value,
            fontWeight: heading.fontWeight.value,
            color:
              heading.textColor.value === "custom"
                ? heading.customColor || defaultTypography.heading.textColor.value
                : colorMap[heading.textColor.value],
            padding: getLayoutPadding(contentBlock.headings, "headings"),
          }}>
          <h1>
            Securing Your Teen's Bright Future:
            <br />
            College Planning Tips Inside
          </h1>
        </div>
        <div
          style={{
            fontFamily: getFontFamily(
              subHeading.fontFamily.value,
              subHeading.fallbackFontFamily.value,
            ),
            fontSize: subHeading.fontSize.value || defaultTypography.subHeading.fontSize.value,
            textAlign: (subHeading.alignment ||
              defaultTypography.subHeading.alignment) as AlignmentOptions,
            lineHeight:
              subHeading.lineHeight.value || defaultTypography.subHeading.lineHeight.value,
            fontWeight:
              subHeading.fontWeight.value || defaultTypography.subHeading.fontWeight.value,
            color:
              subHeading.textColor.value === "custom"
                ? subHeading.customColor || defaultTypography.subHeading.textColor.value
                : colorMap[subHeading.textColor.value],
            padding: getLayoutPadding(contentBlock.headings, "headings"),
          }}>
          <h2>Strategies and Expert Tips for College Success</h2>
        </div>
      </div>
      <div style={parapgraphStyles} className="flex flex-col">
        <div>
          <p>Dear [Parents Name],</p>
          <span>
            Navigating the path to college success with your teen just got easier! Explore expert
            tips and essential strategies in our guide,&nbsp;
          </span>
          <span
            style={{
              color:
                paragraph.linkColor.value === "custom"
                  ? paragraph.customLinkColor || defaultTypography.paragraph.linkColor.value
                  : colorMap[paragraph.linkColor.value],
            }}
            className="underline">
            "Securing Your Teen's Bright Future: College Planning Tips Inside."
          </span>
        </div>
        <div>
          <p className="font-bold pt-5">In this comprehensive guide, you'll discover:</p>
          <ul className="list-disc list-outside pl-7">
            <li>Proven methods for effective college planning.</li>
            <li>Insider tips to streamline the application process.</li>
            <li>Guidance on selecting the right-fit college for your teen.</li>
            <li>Strategies to ensure a successful college transition.</li>
          </ul>
        </div>
        <div>
          <p>Your teen's bright future starts here. Don't miss out on these valuable insights.</p>
          <p>Empower your parenting journey with actionable advice. Read the guide now!</p>
        </div>
      </div>
      <div
        className="flex w-full"
        style={{
          justifyContent: alignment,
          padding: getLayoutPadding(contentBlock.button, "button"),
        }}>
        <button
          type="button"
          className="w-fit font-bold rounded cursor-default outline-none"
          style={{
            fontFamily: getFontFamily(
              buttonLabel.fontFamily.value,
              buttonLabel.fallbackFontFamily.value,
            ),
            fontSize: buttonLabel.fontSize.value || defaultTypography.buttonLabel.fontSize.value,
            lineHeight:
              buttonLabel.lineHeight.value || defaultTypography.buttonLabel.lineHeight.value,
            fontWeight:
              buttonLabel.fontWeight.value || defaultTypography.buttonLabel.fontWeight.value,
            color:
              buttonLabel.textColor.value === "custom"
                ? buttonLabel.customColor || defaultTypography.buttonLabel.textColor.value
                : colorMap[buttonLabel.textColor.value],
            padding: getSimplePadding(padding),
            width: getButtonWidth(),
            background: getColorValue(
              backgroundColor?.name?.value,
              defaultButton.backgroundColor?.name?.value,
              backgroundColor?.custom,
            ),
            borderRadius:
              borderRadius?.name?.value === "custom"
                ? borderRadius?.custom || defaultButton?.borderRadius?.name?.value
                : borderRadius?.name?.value,
            borderStyle: borderStyle?.name?.value || defaultButton?.borderStyle?.name?.value,
            borderWidth: `${borderStyle?.width ?? defaultButton?.borderStyle?.width}px`,
            borderColor: getColorValue(
              borderStyle?.color?.value,
              defaultButton.borderStyle?.color?.value,
              borderStyle?.custom,
            ),
          }}>
          Get the Guide
        </button>
      </div>
      <div style={parapgraphStyles}>
        <p>Best Regards,</p>
        <p>{clientName}</p>
      </div>
      <div style={parapgraphStyles}>
        <p>
          P.S. Securing your teen's future is just a click away. Read the guide for a smoother
          college planning experience.
        </p>
      </div>
    </div>
  );
};

export default PreviewAreaContent;
